import { Injectable } from '@angular/core';

export interface ITabViewController {
    show: () => void;
    hide: () => void;
}

export const enum RcTabIndexes {
    undefined = -1,
    remoteControl = 0,
    snapshot = 1,
    legacyDeviceInfo = 2,
    fileExplorer = 3,
    taskManager = 4,
    webConsole = 5,
    terminal = 6
}

/**
 * Interface definition for the state of a single remote control tab.
 */
export interface ITabViewState {
    enabled: boolean;
    selected: boolean;
    tabTitle: string;
    icon: string;
    show: () => void;
    hide: () => void;
}

/**
 * Class that contains the state of all remote control tabs.
 */
export class TabSelectorState {
    public tabs: ITabViewState[] = [];
    public selectedTab: number = RcTabIndexes.undefined; // we need to know when a tab was loaded (0 is tab RC)

    public resetAll = () => {
        if (this.tabs.length > 0) {
            for (const cur of this.tabs) {
                cur.hide();
            }
        }
    };

    public select = (index: number) => {
        if (this.tabs.length > 0) {
            this.resetAll();
            this.tabs[index].show();
            this.selectedTab = index;
        }
    };

    public addView = (tabView: ITabViewState) => {
        const index: number = this.tabs.length;
        this.tabs.push(tabView);
        if (index === this.selectedTab) {
            this.select(this.selectedTab);
        } else {
          tabView.selected = false;
        }
    };

    public updateView = (tabView: ITabViewState, index: number) => {
        const oldView: ITabViewState = this.tabs[index];
        this.tabs[index] = tabView;
        this.tabs[index].enabled = oldView.enabled;
        this.tabs[index].selected = oldView.selected;
        this.tabs[index].tabTitle = oldView.tabTitle;
        this.tabs[index].icon = oldView.icon;
    };

    public isDisabled = (index: number): boolean => !this.tabs[index].enabled;

    public transfer = (fromState: TabSelectorState) => {
        for (let i = 0; i < fromState.tabs.length && i < this.tabs.length; ++i) {
            const toTabState = this.tabs[i];
            const fromTabState = fromState.tabs[i];
            toTabState.enabled = fromTabState.enabled;
            toTabState.selected = fromTabState.selected;
            toTabState.icon = fromTabState.icon;
            toTabState.tabTitle = fromTabState.tabTitle;
        }
        this.selectedTab = fromState.selectedTab;
    };

    public showSelected = () => {
        const tab = this.tabs[this.selectedTab];
        if (tab) {
            tab.show();
        }
    };
}

/**
 * Remote Control tab service interface definition.
 *
 * The tab service manages the state of remote control view tabs for all rc sessions mapped to a device id.
 */
export interface IRemoteControlTabService {

    /**
     * Gets the remote control tabs state for the given device id.
     *
     * @param deviceId The device id.
     * @return The tab state.
     */
    getState: (deviceId: string) => TabSelectorState;

    /**
     * Resets the tab state state for the given device id.
     *
     * @param event The angular event that the request originated from (optional).
     * @param deviceId The device id.
     */
    resetState: (event: ng.IAngularEvent, deviceId: string) => void;

    /**
     * Transfers device state from the given tab service to this one.
     *
     * @param deviceId The device id.
     * @param fromService The service to transfer state from.
     */
    transfer: (deviceId: string, fromService: IRemoteControlTabService) => void;
}

class DeviceIdToTabSelectorStateMap {
    [deviceId: string]: TabSelectorState
}

@Injectable()
export class RemoteControlTabService implements IRemoteControlTabService {
    private states: DeviceIdToTabSelectorStateMap;

    constructor(
    ) {
        this.states = new DeviceIdToTabSelectorStateMap();
    }

    public getState = (deviceId: string): TabSelectorState => {
        const keys = Object.keys(this.states);
        for (const key of keys) {
            if (key === deviceId || key.endsWith(deviceId) || deviceId.endsWith(key)) {
                return this.states[key];
            }
        }

        const state = new TabSelectorState();
        this.states[deviceId] = state;
        state.selectedTab = RcTabIndexes.remoteControl;
        return state;
    };

    public resetState = (event: ng.IAngularEvent, deviceId: string) => {
        const state: TabSelectorState = this.states[deviceId];
        if (state) {
            delete this.states[deviceId];
        }
    };

    public transfer = (deviceId: string, fromService: IRemoteControlTabService) => {
        const tabSelectorState = this.getState(deviceId);

        tabSelectorState.transfer(fromService.getState(deviceId));
    };
}
